import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import {
  Button,
  Col,
  Divider,
  Form,
  Icon,
  Input,
  Radio,
  Row,
  Spin,
  Steps,
  Switch,
  Typography,
  notification
} from 'antd'
import { Formik } from 'formik'
import { get } from 'lodash'
import { v4 as uuid_v4 } from 'uuid'
import * as yup from 'yup'

import CustomInput from '../../components/CustomInput/customInput'
import CustomMap from '../../components/CustomMap/customMap'
import CustomSelect from '../../components/CustomSelect/customSelect'
import SelectOrganization from '../../components/SelectOrganization/SelectOrganization'
import { RULES, validRule } from '../../constants/Entitlements'
import injectValidationMethods from '../../util/checkValidation'
import { isValidCnpj, isValidCpf } from '../../util/documentValidator'
import { api, request } from '../../util/handlerRequestUtil'
import { getMessage } from '../../util/IntlMessages'
import { orderObjects } from '../../util/orderObjects'
import { isGR, isRtv } from '../../util/profiles'
import { showConfirmDialog } from '../../util/showConfirmDialog'

import './CreateLocation.less'

const { Step } = Steps
const RadioGroup = Radio.Group

// Main
const CreateCustomerLocation = function ({ history, customerId, local, setIsModalLocationVisible, currentStep }) {
  const compState = {
    current: currentStep,
    formatedCities: [],
    formatedStates: [],
    formatedLocationTypes: [],
    formatedRegions: [],
    formatedDistricts: [],
    formValues: {},
    allContacts: [],
    mapLatitude: undefined,
    mapLongitude: undefined,
    mapZoom: undefined,
    currentMainContactId: null,
    showLoadingOnLocationForm: false
  }

  const [state, setState] = useState(compState)
  const [apiLocation, setApiLocation] = useState()
  const [documentTypes, setDocumentTypes] = useState([])

  const auth = useSelector((reduxState) => reduxState.auth)

  // Params url
  const id = customerId
  const idLocation = local?.id

  async function loadOnMount() {
    let updatedState = state
    if (id && idLocation) {
      setState({
        ...state,
        showLoadingOnLocationForm: true
      })
      updatedState = await retrieveLocations(idLocation)
    }
    await listDocumentTypes()
    const formatedListOfStates = await listStates({ ignorePagination: true, todos: false })

    const formatedListOfLocationTypes = await listLocationTypes()
    const listOfContacts = await listContacts(idLocation)
    const currentMainContact = listOfContacts.find((contact) => contact.isMain)
    const newCurrentMainContactId = currentMainContact ? currentMainContact.id : null

    const newState = {
      ...updatedState,
      allContacts: listOfContacts,
      formatedStates: formatedListOfStates,
      formatedLocationTypes: formatedListOfLocationTypes,
      currentMainContactId: newCurrentMainContactId
    }

    retrieveRegions(newState)
  }

  // Mount/Unmount
  useEffect(() => {
    const hasAccess = validRule(RULES.CUSTOMER_LOCATION) // Não deixa acessar a tela
    if (!hasAccess) {
      return history.push('/home')
    }

    loadOnMount()
  }, [])

  function handleArea(area) {
    return area ? parseFloat(area) : 0
  }

  function getDocumentMask(documentId) {
    const documentType = documentTypes.find((item) => item.value === documentId)
    if (documentType) {
      return documentType.mask
    }
    return null
  }

  // Api requests
  async function retrieveLocations(idLocation) {
    try {
      const response = await request(api.getLocationByID(idLocation))
      setApiLocation(response)
      const district = get(response, 'district')
      const region = get(district, 'region')
      const cityState = get(response, 'cityState')
      const newFormatedCities = [
        {
          id: Number(get(cityState, 'id', null)),
          value: get(cityState, 'cityCode', null),
          description: get(cityState, 'name', null)
        }
      ]

      const newFormValues = {
        active: get(response, 'active'),
        customerLocationName: get(response, 'name'),
        customerLocationTypeOfDocument: get(response, 'documentTypeId'),
        customerLocationDocument: get(response, 'documentNumber'),
        customerLocationZipCode: get(response, 'zipCode'),
        customerLocationAddress: get(response, 'addressDescription'),
        customerLocationNumber: get(response, 'addressNumber'),
        customerLocationComplement: get(response, 'addressComplement'),
        customerLocationNeighborhood: get(response, 'neighborhood'),
        customerLocationState: get(cityState, 'stateAbbreviation'),
        organization: get(region, 'organizationId'),
        // Uso o cityCode só para exibir corretamente a cidade
        // mas e enviado para salvar o cityID
        customerLocationCity: get(cityState, 'cityCode'),
        locationType: get(response, 'locationTypeId'),

        'maps.lat': get(response.coordinate, 'x'),
        'maps.long': get(response.coordinate, 'y'),

        customerLocationRegion: local?.regionId,
        customerLocationDistrict: local?.districtId,
        areaTotal: handleArea(get(response, 'areaTotal'))
      }
      const newState = {
        ...state,
        formatedCities: newFormatedCities,
        formValues: newFormValues,
        mapLatitude: get(response.coordinate, 'x'),
        mapLongitude: get(response.coordinate, 'y')
      }
      return newState
    } catch (error) {
      console.tron.log(error)
    }
  }

  async function listDocumentTypes() {
    await request(api.listDocumentTypes(), true).then((types) => {
      const formatedData = []
      types.map((item) => {
        return formatedData.push({
          value: item.id,
          description: item.name,
          mask: item.mask
        })
      })
      setDocumentTypes(formatedData)
    })
  }

  async function listContacts(locationId) {
    try {
      if (!locationId) {
        return []
      }
      const response = await request(
        api.listContacts({
          include: 'customer',
          customerId: id,
          locationId
        }),
        true
      )
      const formatedRows = response.map((item) => {
        return {
          key: item.id,
          ...item
        }
      })
      return formatedRows
    } catch (error) {
      console.tron.log(error)
    }
  }

  async function retrieveRegions(currentState) {
    const formatedRegions = await request(
      api.listRegionDropDown({
        organizationId: currentState?.formValues?.organization,
        active: true,
        todos: false
      }),
      true
    )

    const formatedDistricts = await request(
      api.getDistrictsDropdown({
        regionId: currentState?.formValues?.customerLocationRegion,
        active: true,
        todos: false
      }),
      true
    )

    setState({
      ...currentState,
      formatedRegions,
      formatedDistricts
    })
  }

  async function retrieveRegionsByOrganizationId(params) {
    return await request(api.listRegionDropDown(params), true)
  }

  async function retrieveDistrictsByRegion(params) {
    const response = await request(api.getDistrictsDropdown(params), true)
    return response
  }

  async function storeLocation(data) {
    try {
      await request(api.createLocationByID(data))
      notification.success({
        description: getMessage('localCreatedSucess'),
        message: getMessage('sweetAlerts.success')
      })
      setIsModalLocationVisible(false)
      setState({ ...state, current: 0, formValues: {} })
    } catch (error) {
      console.tron.log(error)
    }
  }

  async function removeContact(id) {
    try {
      if (isNaN(id)) {
        const updatedContacts = state.allContacts.filter((contact) => contact.id !== id)
        setState({ ...state, allContacts: updatedContacts })
        return
      }

      await request(api.destroyContacts(id))
      notification.success({
        description: getMessage('successRemoveContact'),
        message: getMessage('sweetAlerts.success')
      })
      const listOfContacts = await listContacts(idLocation)
      setState({ ...state, allContacts: listOfContacts })
    } catch (error) {
      console.tron.log(error)
    }
  }

  async function updateLocation(idLocation, data) {
    try {
      await request(api.updateLocationByID(idLocation, data))
      notification.success({
        description: getMessage('localUpdatedSucess'),
        message: getMessage('sweetAlerts.success')
      })
      setIsModalLocationVisible(false)
      setState({ ...state, current: 0, formValues: {} })
      setIsModalLocationVisible(false)
    } catch (error) {
      console.tron.log(error)
    }
  }

  async function updateContact(id, data) {
    try {
      request(api.updateContacts(id, data), true)
    } catch (error) {
      console.tron.log(error)
    }
  }

  async function getCepData(params) {
    try {
      const response = await request(api.getCepData(params), true)
      const cepData = {
        customerLocationAddress: get(response, 'logradouro'),
        customerLocationComplement: get(response, 'complemento'),
        customerLocationNeighborhood: get(response, 'bairro'),
        customerLocationState: get(response, 'uf'),
        customerLocationCity: Number(get(response, 'ibge'))
      }
      return cepData
    } catch (error) {
      console.tron.log(error)
    }
  }

  async function listStates(params) {
    try {
      const response = await request(api.listStates(params), true)
      return response
    } catch (error) {
      console.tron.log(error)
    }
  }

  async function listLocationTypes() {
    try {
      const response = await request(api.listLocationTypeDropdwon({ todos: false, active: true }), true)
      return response
    } catch (error) {
      console.tron.log(error)
    }
  }

  async function listCities(params) {
    try {
      const response = await request(api.listCities(params), true)
      const formatedData = response.map((item) => {
        return {
          value: item.cityCode,
          id: item.id,
          description: item.name
        }
      })
      formatedData.sort(orderObjects('description'))
      return formatedData
    } catch (error) {
      console.tron.log(error)
    }
  }

  async function onChangeCustomerLocationState(value) {
    if (state.formValues.customerLocationState !== value) {
      const listOfCities = await listCities({
        ignorePagination: true,
        stateAbbreviation: value
      })

      setState({
        ...state,
        formValues: {
          ...state.formValues,
          customerLocationState: value,
          customerLocationCity: undefined
        },
        formatedCities: listOfCities
      })
    }
  }

  async function onChangeOrganization(_, eventValue) {
    if (!eventValue) {
      return setState({
        ...state,
        formValues: {
          ...state.formValues,
          organization: undefined,
          customerLocationRegion: undefined,
          customerLocationDistrict: undefined
        }
      })
    }

    if (state.organization !== eventValue) {
      const regionItems = await retrieveRegionsByOrganizationId({
        organizationId: eventValue,
        active: true,
        todos: false
      })

      setState({
        ...state,
        formValues: {
          ...state.formValues,
          organization: eventValue,
          customerLocationRegion: undefined,
          customerLocationDistrict: undefined
        },
        formatedRegions: regionItems
      })
    }
  }

  async function onChangeCustomerLocationRegion(_, eventValue) {
    if (state.formValues.customerLocationRegion !== eventValue) {
      const districts = await retrieveDistrictsByRegion({ regionId: eventValue, active: true, todos: false })

      setState({
        ...state,
        formValues: {
          ...state.formValues,
          customerLocationRegion: eventValue,
          customerLocationDistrict: undefined
        },
        formatedDistricts: districts
      })
    }
  }

  async function onChangeCustomerLocationZipCode(_, eventValue) {
    if (!eventValue) {
      return setState({
        ...state,
        formValues: {
          ...state.formValues,
          customerLocationZipCode: undefined,
          customerLocationState: undefined,
          customerLocationCity: undefined
        }
      })
    }

    const customerLocationZipCode = eventValue.replace(/[_-]/g, '')
    if (customerLocationZipCode.length === 8) {
      setState({
        ...state,
        formValues: {
          ...state.formValues,
          customerLocationZipCode,
          customerLocationState: getMessage('loading...'),
          customerLocationCity: getMessage('loading...')
        }
      })

      const cepData = await getCepData(customerLocationZipCode)
      const listOfCities = await listCities({
        ignorePagination: true,
        stateAbbreviation: cepData?.customerLocationState
      })

      const customerLocationCity = listOfCities.find(({ value }) => value === cepData?.customerLocationCity)?.value
      const customerLocationState = cepData.customerLocationState

      setState({
        ...state,
        formValues: {
          ...state.formValues,
          customerLocationZipCode,
          customerLocationState,
          customerLocationCity
        },
        formatedCities: listOfCities
      })
    }
  }

  async function onChangeMapData(mapData) {
    if (mapData.mapLatitude && mapData.mapLongitude && mapData.mapZoom) {
      const newFormValues = {
        ...state.formValues,
        'maps.lat': mapData.mapLatitude,
        'maps.long': mapData.mapLongitude,
        'maps.zoom': mapData.mapZoom
      }
      if (
        state.mapLatitude !== mapData.mapLatitude ||
        state.mapLongitude !== mapData.mapLongitude ||
        state.mapZoom !== mapData.mapZoom
      ) {
        setState({ ...state, formValues: newFormValues })
      }
    }
  }

  async function onChangeIsMainContact(event) {
    // Se o id for um UUID o contato ainda nao foi enviado para o servico
    // so atualizo ele do array
    const updatedContacts = state.allContacts.map((contact) => {
      let isMain = false
      if (contact.id === event) {
        isMain = true
      }

      if (contact.isMain) {
        updateContact(contact.id, { isMain: false })
      }

      return {
        ...contact,
        isMain
      }
    })
    if (!isNaN(event)) {
      updateContact(event, { isMain: true })
    }

    setState({
      ...state,
      currentMainContactId: event,
      allContacts: updatedContacts
    })
  }

  const checkIfValueExists = (value) => value !== null && value !== ''

  const form = {
    initialValues: state.formValues,
    yupValidationCreate: yup.object().shape({
      customerLocationName: yup
        .string()
        .max(60)
        .typeError(getMessage('shouldBeString'))
        .required(getMessage('isRequired', getMessage('customerName'))),
      customerLocationTypeOfDocument: yup
        .string()
        .max(20)
        .typeError(getMessage('shouldBeString'))
        .required(getMessage('shouldSelectAValue')),
      customerLocationDocument: yup
        .string()
        .max(20)
        .typeError(getMessage('shouldBeNumber'))
        .required(getMessage('isRequired', getMessage('customerLocationDocument')))
        .when('customerLocationTypeOfDocument', (customerLocationTypeOfDocument, schema) => {
          if (+customerLocationTypeOfDocument === documentTypes.find((type) => type.description === 'CPF').value) {
            return schema.test('customerLocationDocument', getMessage('cpfInvalid'), (value) => {
              return isValidCpf(value)
            })
          }

          if (+customerLocationTypeOfDocument === documentTypes.find((type) => type.description === 'CNPJ').value) {
            return schema.test('customerLocationDocument', getMessage('cnpjInvalid'), (value) => {
              return isValidCnpj(value)
            })
          }

          return schema
        }),
      customerLocationZipCode: yup
        .string()
        .max(9)
        .typeError(getMessage('shouldBeString'))
        .required(getMessage('isRequired', getMessage('customerLocationZipCode'))),
      customerLocationState: yup
        .string()
        .required(getMessage('selectTheState'))
        .test('customerLocationState', getMessage('selectTheState'), checkIfValueExists),
      customerLocationCity: yup
        .string()
        .required(getMessage('selectTheCity'))
        .test('customerLocationCity', getMessage('selectTheCity'), checkIfValueExists),
      locationType: yup
        .string()
        .typeError(getMessage('selectTheLocationType'))
        .required(getMessage('selectTheLocationType'))
        .test('locationType', getMessage('selectTheLocationType'), checkIfValueExists),
      organization: yup
        .string()
        .required(getMessage('selectTheOrganization'))
        .test('organization', getMessage('selectTheOrganization'), checkIfValueExists),
      customerLocationRegion: yup
        .string()
        .required(getMessage('selectTheRegion'))
        .test('customerLocationRegion', getMessage('selectTheRegion'), checkIfValueExists),
      customerLocationDistrict: yup
        .string()
        .required(getMessage('selectTheDistrict'))
        .test('customerLocationDistrict', getMessage('selectTheDistrict'), checkIfValueExists)
    }),
    yupValidationUpdate: yup.object().shape({
      customerLocationName: yup
        .string()
        .max(60)
        .typeError(getMessage('shouldBeString'))
        .required(getMessage('isRequired', getMessage('customerName'))),
      customerLocationDocument: yup
        .string()
        .max(20)
        .typeError(getMessage('shouldBeNumber'))
        .required(getMessage('isRequired', getMessage('customerLocationDocument')))
        .when('customerLocationTypeOfDocument', (customerLocationTypeOfDocument, schema) => {
          if (+customerLocationTypeOfDocument === documentTypes.find((type) => type.description === 'CPF').value) {
            console.tron.log('isCpf')
            return schema.test('customerLocationDocument', getMessage('cpfInvalid'), (value) => {
              return isValidCpf(value)
            })
          }

          if (+customerLocationTypeOfDocument === documentTypes.find((type) => type.description === 'CNPJ').value) {
            return schema.test('customerLocationDocument', getMessage('cnpjInvalid'), (value) => {
              return isValidCnpj(value)
            })
          }
          return schema
        }),
      customerLocationTypeOfDocument: yup
        .number()
        .typeError(getMessage('shouldBeNumber'))
        .required(getMessage('shouldSelectAValue')),
      customerLocationZipCode: yup
        .string()
        .max(9)
        .typeError(getMessage('shouldBeString'))
        .required(getMessage('isRequired', getMessage('customerLocationZipCode'))),
      customerLocationState: yup
        .string()
        .required(getMessage('selectTheState'))
        .test('customerLocationState', getMessage('selectTheState'), checkIfValueExists),
      customerLocationCity: yup
        .string()
        .required(getMessage('selectTheCity'))
        .test('customerLocationCity', getMessage('selectTheCity'), checkIfValueExists),
      locationType: yup
        .string()
        .typeError(getMessage('selectTheLocationType'))
        .required(getMessage('selectTheLocationType'))
        .test('locationType', getMessage('selectTheLocationType'), checkIfValueExists),
      organization: yup
        .string()
        .required(getMessage('selectTheOrganization'))
        .test('organization', getMessage('selectTheOrganization'), checkIfValueExists),
      customerLocationRegion: yup
        .string()
        .required(getMessage('selectTheRegion'))
        .test('customerLocationRegion', getMessage('selectTheRegion'), checkIfValueExists),
      customerLocationDistrict: yup
        .string()
        .required(getMessage('selectTheDistrict'))
        .test('customerLocationDistrict', getMessage('selectTheDistrict'), checkIfValueExists)
    }),
    onSubmit: async (values) => {
      const city = state.formatedCities.find((currentCity) => currentCity.value === values.customerLocationCity)
      const zipCode = values.customerLocationZipCode ? get(values, 'customerLocationZipCode') : ''
      const addressDescription = values.customerLocationAddress ? get(values, 'customerLocationAddress') : ''
      const addressComplement = values.customerLocationComplement ? get(values, 'customerLocationComplement') : ''
      const neighborhood = values.customerLocationNeighborhood ? get(values, 'customerLocationNeighborhood') : ''
      const addressNumber = values.customerLocationNumber ? get(values, 'customerLocationNumber') : ''

      const regionId = values.customerLocationRegion ? parseInt(get(values, 'customerLocationRegion')) : ''
      const districtId = values.customerLocationDistrict ? parseInt(get(values, 'customerLocationDistrict')) : ''

      let data = {
        active: get(values, 'active'),
        addressComplement,
        addressDescription,
        addressNumber,
        allContacts: state.allContacts,
        areaTotal: handleArea(get(values, 'areaTotal')),
        cityStateId: Number(get(city, 'id', null)) || '',
        customerId: parseInt(id),
        districtId,
        documentNumber: get(values, 'customerLocationDocument'),
        documentTypeId: parseInt(get(values, 'customerLocationTypeOfDocument')),
        locationTypeId: parseInt(get(values, 'locationType')),
        name: get(values, 'customerLocationName'),
        neighborhood,
        regionId,
        zipCode
      }

      if (state.allContacts && state.allContacts.length > 0) {
        const mainContact = state.allContacts.find((contact) => contact.isMain === true)
        data.phoneNumber = mainContact ? mainContact.phoneNumber : null
      }

      if (values['maps.long'] || values['maps.lat']) {
        data = {
          ...data,
          coordinate: get(values, 'maps.lat', 0) + ',' + get(values, 'maps.long', 0)
        }
      }

      if (idLocation) {
        console.tron.log('submit values', data)

        const rtvChangedRegionOrDistrict = checkRTVChangedRegionOrDistrict(data)
        if (rtvChangedRegionOrDistrict) {
          return updateLocation(idLocation, data)
        }

        return updateLocation(idLocation, data)
      }

      console.tron.log('submit values', data)
      return storeLocation(data)
    }
  }

  function checkRTVChangedRegionOrDistrict({ districtId, regionId }) {
    if (!isRtv(auth.auth)) return false

    const { district } = apiLocation
    if (!district) return true

    return `${districtId}` !== district.id || `${regionId}` !== district.regionId
  }

  const yupValidation = !idLocation ? form.yupValidationCreate : form.yupValidationUpdate

  function renderGeneralInformations(errors, setFieldValueWithState, values, handleBlur, documentTypeMask) {
    return (
      <Spin style={{ color: 'red' }} spinning={state.showLoadingOnLocationForm}>
        <div className="createLocation__generalInformationsWrapper">
          <div className="createLocation__activeSwitchWrapper">
            {getMessage('localstatus')}
            <Switch
              className="createLocation__activeSwitch"
              size="default"
              checked={values.active}
              onChange={(value) => {
                setFieldValueWithState('active', value)
              }}
            />
          </div>

          <div className="createLocation__verticalLine" />

          <div className="createLocation__informationsWrapper">
            <CustomInput
              required
              field="customerLocationName"
              errors={errors}
              setFieldValue={setFieldValueWithState}
              rowSpan={16}
              value={values}
              handleBlur={handleBlur}
              label={`${getMessage('contactName')}`}
            />
            <CustomSelect
              showSearch
              required
              field="customerLocationTypeOfDocument"
              errors={errors}
              setFieldValue={setFieldValueWithState}
              rowSpan={16}
              value={values}
              values={documentTypes}
              handleBlur={handleBlur}
              label={`${getMessage('documentType')}`}
            />
            <CustomInput
              field="customerLocationDocument"
              required
              errors={errors}
              setFieldValue={setFieldValueWithState}
              rowSpan={16}
              value={values}
              handleBlur={handleBlur}
              mask={documentTypeMask}
              label={`${getMessage('customerLocationDocument')}`}
            />
          </div>
        </div>
      </Spin>
    )
  }

  function renderContacts(errors, setFieldValueWithState, values, handleBlur, hasAccess, setFieldError) {
    return (
      <>
        <div className="createLocation__contactsWrapper">
          <Row type="flex" justify="center">
            <CustomInput
              field="contactName"
              errors={errors}
              required={true}
              setFieldValue={setFieldValueWithState}
              rowSpan={5}
              value={values}
              handleBlur={handleBlur}
              label={`${getMessage('contactName')}`}
            />
            <CustomInput
              field="phoneNumber"
              errors={errors}
              setFieldValue={setFieldValueWithState}
              rowSpan={4}
              value={values}
              handleBlur={handleBlur}
              mask={'(11)11111-1111'}
            />
            <CustomInput
              field="email"
              errors={errors}
              setFieldValue={setFieldValueWithState}
              rowSpan={5}
              value={values}
              handleBlur={handleBlur}
            />
            <CustomInput
              field="jobRole"
              errors={errors}
              setFieldValue={setFieldValueWithState}
              rowSpan={5}
              value={values}
              handleBlur={handleBlur}
            />
            {hasAccess && (
              <Col rowSpan={3}>
                <Button
                  type="dashed"
                  style={{ marginTop: 22 }}
                  onClick={() => {
                    let canAddContact = true
                    if ([null, undefined, ''].includes(state.formValues.contactName)) {
                      setFieldError('contactName', getMessage('mandatoryField'))
                      canAddContact = false
                    }

                    if (canAddContact) {
                      const generatedContactId = uuid_v4()
                      const updatedContacts = [
                        ...state.allContacts,
                        {
                          active: true,
                          name: state.formValues.contactName,
                          customerId: id,
                          email: state.formValues.email,
                          id: generatedContactId,
                          jobRole: state.formValues.jobRole,
                          key: generatedContactId,
                          locationId: idLocation,
                          phoneNumber: state.formValues.phoneNumber,
                          isMain: state.allContacts.length === 0
                        }
                      ]
                      const updatedFormValues = {
                        ...state.formValues,
                        contactName: '',
                        email: '',
                        phoneNumber: '',
                        jobRole: ''
                      }
                      setState({
                        ...state,
                        allContacts: updatedContacts,
                        formValues: updatedFormValues,
                        currentMainContactId:
                          updatedContacts.length === 1 ? generatedContactId : state.currentMainContactId
                      })
                    }
                  }}
                >
                  <Icon type="plus" style={{ color: '#fff', fontSize: 20, fontWeight: 200 }} />
                  {getMessage('addContact')}
                </Button>
              </Col>
            )}
          </Row>
          <RadioGroup name="radiogroup" value={state.currentMainContactId} className="createLocation__contactsList">
            {state.allContacts.map((contact, index) => (
              <div key={contact.id}>
                {index === 0 && <Divider />}
                <Row type="flex" justify="center">
                  <Col span={5}>
                    <Typography className="list-contacts" level={3}>
                      {getMessage('contactName')}
                    </Typography>
                    <Input disabled value={contact.name} />
                  </Col>
                  <Col span={4}>
                    <Typography className="list-contacts" level={3}>
                      {getMessage('phoneNumber')}
                    </Typography>
                    <Input disabled value={contact.phoneNumber} />
                  </Col>
                  <Col span={5}>
                    <Typography className="list-contacts" level={3}>
                      {getMessage('email')}
                    </Typography>
                    <Input disabled value={contact.email} />
                  </Col>
                  <Col span={5}>
                    <Typography className="list-contacts" level={3}>
                      {getMessage('jobRole')}
                    </Typography>
                    <Input disabled value={contact.jobRole} />
                  </Col>
                  <Col style={{ display: 'flex', marginTop: 35 }}>
                    <Radio value={contact.id} onChange={(event) => onChangeIsMainContact(event.target.value)} />
                    <Typography level={3}>{getMessage('principal')}</Typography>
                  </Col>
                  <Col span={2} style={{ marginTop: 23 }}>
                    <Button
                      onClick={() => {
                        showConfirmDialog(() => {
                          removeContact(contact.id)
                        })
                      }}
                      style={{ borderRadius: 3, borderColor: '#d9d9d9', padding: 0, width: 36 }}
                    >
                      <Icon type="delete" style={{ color: '#818287', fontSize: 23, marginTop: 5, width: 22 }} />
                    </Button>
                  </Col>
                </Row>
              </div>
            ))}
          </RadioGroup>
        </div>
      </>
    )
  }

  function renderAddress(errors, setFieldValueWithState, values, handleBlur) {
    const shouldDisableState =
      values.customerLocationZipCode === undefined || values?.customerLocationZipCode?.length < 8
    const shouldDisableCity = values.customerLocationState === undefined
    const shouldDisableRegion = values.organization === undefined || isGR(auth)
    const shouldDisableDistrict = values.customerLocationRegion === undefined

    return (
      <div className="createLocation__addressWrapper">
        <div className="createLocation__mapWrapper">
          <CustomMap
            latitude={state.mapLatitude}
            longitude={state.mapLongitude}
            zoom={8}
            className="leaflet-container-map"
            callbackData={onChangeMapData}
          />
          <div className="createLocation__mapInputsWrapper">
            <CustomInput
              className="createLocation__mapInputs"
              field="maps.lat"
              errors={errors}
              setFieldValue={setFieldValueWithState}
              rowSpan={11}
              value={values}
              handleBlur={(e) => {
                const latitude = e.target.value.replace(/[a-zA-Z]+/g, '')
                setState({ ...state, mapLatitude: latitude })
                handleBlur(e)
              }}
            />
            <CustomInput
              className="createLocation__mapInputs"
              field="maps.long"
              errors={errors}
              setFieldValue={setFieldValueWithState}
              rowSpan={11}
              value={values}
              handleBlur={(e) => {
                const longitude = e.target.value.replace(/[a-zA-Z]+/g, '')
                setState({ ...state, mapLongitude: longitude })
                handleBlur(e)
              }}
            />
          </div>
        </div>

        <div className="createLocation__verticalLine" />

        <div className="createLocation__addressInputsWrapper">
          <Row type="flex" justify="start">
            <CustomInput
              required
              field="customerLocationZipCode"
              errors={errors}
              setFieldValue={setFieldValueWithState}
              rowSpan={12}
              value={values}
              onChangeWithField={onChangeCustomerLocationZipCode}
              handleBlur={handleBlur}
              mask="11111-111"
            />
            <CustomSelect
              required
              showSearch
              field="customerLocationState"
              errors={errors}
              setFieldValue={setFieldValueWithState}
              rowSpan={12}
              value={values}
              onChange={onChangeCustomerLocationState}
              values={state.formatedStates}
              enable={shouldDisableState}
            />
          </Row>
          <Row type="flex" justify="start">
            <CustomSelect
              required
              showSearch
              field="customerLocationCity"
              errors={errors}
              setFieldValue={setFieldValueWithState}
              rowSpan={24}
              value={values}
              values={state.formatedCities}
              enable={shouldDisableCity}
            />
          </Row>
          <Row type="flex" justify="start">
            <CustomInput
              field="customerLocationAddress"
              errors={errors}
              setFieldValue={setFieldValueWithState}
              rowSpan={16}
              value={values}
              handleBlur={handleBlur}
            />
            <CustomInput
              field="customerLocationNumber"
              errors={errors}
              setFieldValue={setFieldValueWithState}
              rowSpan={8}
              value={values}
              handleBlur={handleBlur}
            />
          </Row>
          <Row type="flex" justify="start">
            <CustomInput
              field="customerLocationComplement"
              errors={errors}
              setFieldValue={setFieldValueWithState}
              rowSpan={12}
              value={values}
              handleBlur={handleBlur}
            />
            <CustomInput
              field="customerLocationNeighborhood"
              errors={errors}
              setFieldValue={setFieldValueWithState}
              rowSpan={12}
              value={values}
              handleBlur={handleBlur}
            />
          </Row>
          <Row type="flex" justify="start">
            <CustomSelect
              showSearch
              field="locationType"
              errors={errors}
              setFieldValue={setFieldValueWithState}
              rowSpan={12}
              required
              value={values}
              values={state.formatedLocationTypes}
            />
            <SelectOrganization
              required
              errors={errors}
              field="organization"
              setFieldValue={setFieldValueWithState}
              handleBlur={form.handleBlur}
              formValues={values}
              rowSpan={12}
              onChangeWithField={onChangeOrganization}
              todos={false}
            />
          </Row>
          <Row type="flex" justify="start">
            <CustomSelect
              enable={shouldDisableRegion}
              showSearch
              field="customerLocationRegion"
              errors={errors}
              setFieldValue={setFieldValueWithState}
              rowSpan={12}
              value={values}
              onChangeWithField={onChangeCustomerLocationRegion}
              values={state.formatedRegions}
              required
            />
            <CustomSelect
              enable={shouldDisableDistrict}
              showSearch
              field="customerLocationDistrict"
              errors={errors}
              setFieldValue={setFieldValueWithState}
              rowSpan={12}
              value={values}
              values={state.formatedDistricts}
              required
            />
          </Row>
          <Row type="flex" justify="start">
            <CustomInput
              field="areaTotal"
              errors={errors}
              setFieldValue={setFieldValueWithState}
              rowSpan={24}
              value={values}
              type="number"
              defaultValue={0}
              handleBlur={handleBlur}
            />
          </Row>
        </div>
      </div>
    )
  }

  return (
    <>
      <Formik
        enableReinitialize
        validationSchema={yupValidation}
        initialValues={state.formValues}
        onSubmit={(values, { setSubmitting, setFieldError, resetForm }) =>
          form.onSubmit(values, setSubmitting, setFieldError, resetForm)
        }
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ values, errors, handleBlur, handleSubmit, setFieldValue, submitForm, setFieldError }) => {
          injectValidationMethods(values, yupValidation)
          let documentTypeMask = getDocumentMask(values.customerLocationTypeOfDocument)
          const setFieldValueWithState = (field, value) => {
            const newFormValues = {
              ...state.formValues,
              [field]: value
            }
            setState({ ...state, formValues: newFormValues })
            return setFieldValue(field, value)
          }

          const hasAccess =
            idLocation === null ? validRule(RULES.CUSTOMER_LOCATION) : validRule(RULES.CUSTOMER_LOCATION)

          const steps = [
            {
              title: getMessage('customer.title.info'),
              content: renderGeneralInformations(errors, setFieldValueWithState, values, handleBlur, documentTypeMask)
            },
            {
              title: getMessage('Contacts'),
              content: renderContacts(errors, setFieldValueWithState, values, handleBlur, hasAccess, setFieldError)
            },
            {
              title: getMessage('tab.address'),
              content: renderAddress(errors, setFieldValueWithState, values, handleBlur)
            }
          ]

          const onChangeStep = (current) => {
            setState({ ...state, current })
          }

          const onClickNext = async () => {
            let canGoNext = true

            if (state.current === 0) {
              await Promise.all([
                form.yupValidationCreate.validateAt('customerLocationName', values).catch((error) => {
                  setFieldError('customerLocationName', error.message)
                  canGoNext = false
                }),
                form.yupValidationCreate.validateAt('customerLocationTypeOfDocument', values).catch((error) => {
                  setFieldError('customerLocationTypeOfDocument', error.message)
                  canGoNext = false
                }),
                form.yupValidationCreate.validateAt('customerLocationDocument', values).catch((error) => {
                  setFieldError('customerLocationDocument', error.message)
                  canGoNext = false
                })
              ])
            }

            if (state.current === 1 && state.allContacts.length === 0) {
              setFieldError('contactName', getMessage('mandatoryField'))
              canGoNext = false
            }

            if (canGoNext) {
              setState({ ...state, current: state.current + 1 })
            }
          }

          return (
            <Form className="login-form" layout="vertical" onSubmit={handleSubmit}>
              <>
                <Steps
                  className="custome-step"
                  style={{ width: '50%', margin: '0 auto' }}
                  onChange={onChangeStep}
                  current={state.current}
                >
                  {steps.map((item) => (
                    <Step key={item.title} title={item.title} disabled />
                  ))}
                </Steps>
                <div className="steps-content">{steps[state.current].content}</div>
                <div className="horizontal-line"></div>
                <div
                  style={{
                    flex: 1,
                    justifyContent: 'flex-end',
                    flexDirection: 'row',
                    display: 'flex',
                    marginTop: '2%'
                  }}
                >
                  {state.current !== 0 && (
                    <Button
                      style={{
                        width: '15%',
                        borderColor: '#EE778F',
                        color: '#EE778F'
                      }}
                      type="ghost"
                      onClick={() => setState({ ...state, current: state.current - 1 })}
                    >
                      {getMessage('back')}
                    </Button>
                  )}
                  {hasAccess && (
                    <>
                      <Button type="primary" className="button-action" onClick={() => submitForm()}>
                        {idLocation ? getMessage('update') : getMessage('register')}
                      </Button>

                      {state.current < 2 && (
                        <Button type="dashed" className="button-action" onClick={onClickNext}>
                          {getMessage('next')}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </>
            </Form>
          )
        }}
      </Formik>
    </>
  )
}

export default CreateCustomerLocation
